/**
 * The browser's voice. Every sound is synthesized with WebAudio: nothing to fetch, nothing to
 * precache in the PWA, and the vocabulary is the one `apps/android-native/.../sound/CallSounds.kt`
 * speaks — the same two falling notes for a message (`res/raw/olmt_message.ogg` there), a soft
 * repeating ring, a low ringback, and the small join/leave/end pair.
 *
 * What rings when: `IncomingCallRing` owns the ring loop's lifetime, `CallStage` owns the ringback,
 * the participant tones and the end tone, and `socket.ts` owns the message blip — every *decision*
 * lives in `soundRules.ts`, so this module is only the instrument.
 *
 * Autoplay policy: a context may only make sound after the first user gesture. The module arms
 * itself on the first pointer or key event anywhere in the app, and until then every call here is a
 * silent no-op — which is also the honest behaviour for a tab nobody has touched (the OS
 * notification is what reaches that user, with the system's own sound).
 */
import type { AppBlip } from './soundRules'

type Note = { hz: number; at: number; dur: number; gain: number }

/** The message blip: E6 → B5, the same two notes the Android channel sound carries. */
const MESSAGE_NOTES: Note[] = [
  { hz: 1318.51, at: 0, dur: 0.22, gain: 0.16 },
  { hz: 987.77, at: 0.13, dur: 0.42, gain: 0.18 },
]

/** The send tick: one quiet note, quick enough not to become a metronome while typing. */
const SENT_NOTES: Note[] = [{ hz: 987.77, at: 0, dur: 0.09, gain: 0.05 }]

/** Ascending — someone arrived. */
const JOIN_NOTES: Note[] = [
  { hz: 987.77, at: 0, dur: 0.16, gain: 0.13 },
  { hz: 1318.51, at: 0.11, dur: 0.3, gain: 0.15 },
]

const LEAVE_NOTES: Note[] = [{ hz: 783.99, at: 0, dur: 0.3, gain: 0.11 }]

/** Descending — the call is over. */
const END_NOTES: Note[] = [
  { hz: 880, at: 0, dur: 0.18, gain: 0.14 },
  { hz: 659.25, at: 0.12, dur: 0.4, gain: 0.14 },
]

const BLIP_NOTES: Record<AppBlip, Note[]> = {
  message: MESSAGE_NOTES,
  sent: SENT_NOTES,
  join: JOIN_NOTES,
  leave: LEAVE_NOTES,
  end: END_NOTES,
}

/** The repeating ring: the message pair, slower and longer — a call asks again every cycle. */
const RING_NOTES: Note[] = [
  { hz: 1318.51, at: 0, dur: 0.5, gain: 0.11 },
  { hz: 987.77, at: 0.34, dur: 0.6, gain: 0.11 },
]
const RING_CYCLE_MS = 2400

/** The ringback a caller waits through: the classic 440+480 dual tone, one second per cycle. */
const RINGBACK_NOTES: Note[] = [
  { hz: 440, at: 0, dur: 1, gain: 0.06 },
  { hz: 480, at: 0, dur: 1, gain: 0.06 },
]
const RINGBACK_CYCLE_MS = 3500

let audioContext: AudioContext | null = null

function context(): AudioContext | null {
  if (typeof window === 'undefined') return null
  if (audioContext) return audioContext
  const Ctor =
    window.AudioContext ??
    (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
  if (!Ctor) return null
  audioContext = new Ctor()
  return audioContext
}

function unlock(): void {
  const audio = context()
  if (audio && audio.state === 'suspended') void audio.resume().catch(() => undefined)
}

if (typeof window !== 'undefined') {
  // The first gesture anywhere is what lets the context run; later blips are plain plays.
  window.addEventListener('pointerdown', unlock, { once: true })
  window.addEventListener('keydown', unlock, { once: true })
}

/** One pass of a note list, returning the oscillators so a loop's stop can cut it short. */
function play(notes: Note[]): OscillatorNode[] {
  const audio = context()
  const started: OscillatorNode[] = []
  if (!audio || audio.state === 'suspended') return started
  const now = audio.currentTime
  for (const note of notes) {
    const oscillator = audio.createOscillator()
    const gain = audio.createGain()
    const start = now + note.at
    oscillator.type = 'sine'
    oscillator.frequency.value = note.hz
    gain.gain.setValueAtTime(0, start)
    gain.gain.linearRampToValueAtTime(note.gain, start + 0.008)
    gain.gain.exponentialRampToValueAtTime(0.0001, start + note.dur)
    oscillator.connect(gain)
    gain.connect(audio.destination)
    oscillator.start(start)
    oscillator.stop(start + note.dur + 0.05)
    started.push(oscillator)
  }
  return started
}

type Loop = { timer: number; active: OscillatorNode[] }

function startLoop(notes: Note[], cycleMs: number): Loop {
  const loop: Loop = { timer: 0, active: [] }
  loop.active = play(notes)
  loop.timer = window.setInterval(() => {
    loop.active = play(notes)
  }, cycleMs)
  return loop
}

function stopLoop(loop: Loop | null): void {
  if (!loop) return
  window.clearInterval(loop.timer)
  for (const oscillator of loop.active) {
    // The already-scheduled stop may have run; stopping twice is fine and must not throw.
    try {
      oscillator.stop()
    } catch {
      /* already stopped by its own schedule */
    }
  }
}

let ringLoop: Loop | null = null
let ringbackLoop: Loop | null = null

/** The incoming ring, repeating until `stopRing` — answer, decline, or the call ending. */
export function startRing(): void {
  if (ringLoop) return
  unlock()
  ringLoop = startLoop(RING_NOTES, RING_CYCLE_MS)
}

export function stopRing(): void {
  stopLoop(ringLoop)
  ringLoop = null
}

/** The caller's wait. Started by `CallStage` when the room is connected but empty; stopped by the
 *  first participant (the tone stopping *is* the connected signal) or by the call ending. */
export function startRingback(): void {
  if (ringbackLoop) return
  unlock()
  ringbackLoop = startLoop(RINGBACK_NOTES, RINGBACK_CYCLE_MS)
}

export function stopRingback(): void {
  stopLoop(ringbackLoop)
  ringbackLoop = null
}

/** One of the small tones — see `soundRules.ts` for when each is deserved. */
export function blip(kind: AppBlip): void {
  unlock()
  play(BLIP_NOTES[kind])
}
